/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import {
  BottomSheet,
  CbhIcon,
  Illustration,
  Input,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, FormControl, type InputBaseComponentProps, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { CommonDialog } from "@src/appV2/lib/Dialogs/Common/Dialog";
import { MaskedPhoneInput } from "@src/appV2/lib/FormFields/PhoneField/MaskedPhoneInput";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type FunctionComponent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import {
  type PlacementCandidate,
  PlacementCandidateScreeningStatus,
} from "../../PlacementCandidate/types";
import { type BookingOrderingCohort } from "../hooks/useBookingOrderingCohort";
import { useNotifyRecruiterForScreening } from "../hooks/useNotifyRecruiterForScreening";
import { usePlacementRecruiterScreeningConfig } from "../hooks/usePlacementRecruiterScreeningConfig";
import { PLACEMENT_PATHS } from "../paths";

interface RecruiterScreeningCallDialogProps {
  modalState: UseModalState;
  placementId?: string;
  source?: "banner" | "detail-cta";
  placementCandidate?: PlacementCandidate;
  bookingReorderingCohort: BookingOrderingCohort;
  selectedInterviewTime?: { start: Date; end: Date };
}

type ScreeningStep = "initial" | "phone-confirmation" | "success";

const phoneFormSchema = z.object({
  phone: z
    .string()
    .min(1)
    .refine((v) => v !== "(___) ___-____", {
      // This is what react-imask returns when there is no input
      message: "Please enter a valid phone number",
    }),
});

type PhoneFormData = z.infer<typeof phoneFormSchema>;

function WarningBanner() {
  return (
    <Alert
      severity="warning"
      icon={
        <Box sx={{ margin: "auto", paddingLeft: 3 }}>
          <CbhIcon type="triangle-exclamation" />
        </Box>
      }
      sx={(theme) => ({
        backgroundColor: theme.palette.warning.dark,
        color: theme.palette.warning.contrastText,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        "& .MuiAlert-message": {
          width: "100%",
        },
      })}
    >
      <Text>
        <Text inline sx={{ fontWeight: 800 }}>
          Your interview is not booked
        </Text>{" "}
        until you complete this call with Clipboard. We&apos;ll call you within 15 minutes!
      </Text>
    </Alert>
  );
}

interface CallPurposeSectionProps {
  cohort: "screen_first" | "screen_last" | "book_first";
}

function CallPurposeSection(props: CallPurposeSectionProps) {
  const { cohort } = props;
  const isScreenFirst = cohort === "screen_first";

  return (
    <Stack spacing={2} alignItems="flex-start" sx={{ paddingX: 4 }}>
      <Text variant="body1" textAlign="center" sx={{ fontSize: 18 }}>
        What&apos;s the purpose of this call?
      </Text>
      <Stack spacing={1.5} alignItems="flex-start" sx={{ paddingLeft: 4 }}>
        <Text variant="body1" sx={{ fontSize: 18 }}>
          • One-time 10 minute phone call with Clipboard
        </Text>
        <Text variant="body1" sx={{ fontSize: 18 }}>
          • Discuss our attendance policy
        </Text>
        <Text variant="body1" sx={{ fontSize: 18 }}>
          {isScreenFirst
            ? "• Unlock instant interview listings - book an interview after the call"
            : "• Help you book your first Instant Interview!"}
        </Text>
      </Stack>
    </Stack>
  );
}

interface InitialStepContentProps {
  onCallMeNow: () => void;
  onScheduleLater: () => void;
  isPendingScreening: boolean;
  isNotifyingRecruiter: boolean;
  screeningCalendlyLink?: string;
  cohort: "screen_first" | "screen_last" | "book_first";
  showCallMeNow: boolean;
}

function InitialStepContent(props: InitialStepContentProps) {
  const {
    onCallMeNow,
    onScheduleLater,
    isPendingScreening,
    isNotifyingRecruiter,
    screeningCalendlyLink,
    cohort,
    showCallMeNow,
  } = props;

  const isScreenFirst = cohort === "screen_first";
  const title = isScreenFirst ? "First step: Quick phone call" : "Last step: Quick phone call";

  return (
    <Stack spacing={10} alignItems="center" justifyContent="center">
      <Stack spacing={6} alignItems="center" justifyContent="center">
        <Illustration type="phone" />

        <Text variant="h2" textAlign="center">
          {title}
        </Text>
      </Stack>

      <CallPurposeSection cohort={cohort} />

      {!isScreenFirst && <WarningBanner />}

      <Stack spacing={4} sx={{ width: "100%" }}>
        {showCallMeNow && (
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isNotifyingRecruiter}
            onClick={onCallMeNow}
          >
            Call me now
          </LoadingButton>
        )}

        {!isPendingScreening && (
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={!screeningCalendlyLink}
            onClick={onScheduleLater}
          >
            Schedule the call for later
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

interface PhoneConfirmationStepContentProps {
  form: ReturnType<typeof useForm<PhoneFormData>>;
  onConfirm: (data: PhoneFormData) => void;
  onBack: () => void;
  isNotifyingRecruiter: boolean;
  cohort: "screen_first" | "screen_last" | "book_first";
}

function PhoneConfirmationStepContent(props: PhoneConfirmationStepContentProps) {
  const { form, onConfirm, onBack, isNotifyingRecruiter, cohort } = props;
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const isScreenFirst = cohort === "screen_first";
  const title = isScreenFirst
    ? "Complete call with Clipboard to unlock interviews"
    : "Complete call with Clipboard to book interview";

  return (
    <Stack spacing={6} alignItems="center" justifyContent="center">
      <Stack spacing={4} alignItems="center" justifyContent="center">
        <Illustration type="phone" />

        <Text variant="h2" textAlign="center">
          {title}
        </Text>
      </Stack>

      <Stack spacing={4} sx={{ width: "100%" }}>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Stack spacing={2}>
              <Text semibold variant="body1" sx={{ color: (theme) => theme.palette.grey[700] }}>
                Phone
              </Text>
              <FormControl>
                <Input
                  {...field}
                  fullWidth
                  inputComponent={
                    MaskedPhoneInput as unknown as FunctionComponent<InputBaseComponentProps>
                  }
                  placeholder="(123) 456-7890"
                  type="tel"
                  size="large"
                  error={Boolean(errors.phone)}
                />
              </FormControl>
              {errors.phone && (
                <Text variant="caption" color="error" sx={{ paddingLeft: 1 }}>
                  {errors.phone?.message}
                </Text>
              )}
            </Stack>
          )}
        />
      </Stack>

      {!isScreenFirst && <WarningBanner />}

      <Stack spacing={4} sx={{ width: "100%" }}>
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          isLoading={isNotifyingRecruiter}
          onClick={handleSubmit(onConfirm)}
        >
          Confirm call
        </LoadingButton>

        <Button fullWidth variant="outlined" size="large" onClick={onBack}>
          Back
        </Button>
      </Stack>
    </Stack>
  );
}

interface SuccessStepContentProps {
  onBrowseMoreJobs: () => void;
  cohort: "screen_first" | "screen_last" | "book_first";
}

function SuccessStepContent(props: SuccessStepContentProps) {
  const { onBrowseMoreJobs, cohort } = props;
  const isScreenFirst = cohort === "screen_first";

  return (
    <Stack spacing={10} alignItems="center" justifyContent="center">
      <Stack spacing={6} alignItems="center" justifyContent="center">
        <Illustration type="phone" />

        <Text variant="h2" textAlign="center">
          Speak to you within 15 minutes!
        </Text>
      </Stack>

      <CallPurposeSection cohort={cohort} />

      {!isScreenFirst && <WarningBanner />}

      <Stack spacing={4} sx={{ width: "100%" }}>
        <Button fullWidth variant="contained" size="large" onClick={onBrowseMoreJobs}>
          {isScreenFirst ? "Browse jobs" : "Browse more jobs"}
        </Button>
      </Stack>
    </Stack>
  );
}

export function RecruiterScreeningCallDialog(props: RecruiterScreeningCallDialogProps) {
  const {
    modalState,
    placementId,
    source = "detail-cta",
    placementCandidate,
    bookingReorderingCohort,
    selectedInterviewTime,
  } = props;
  const { screeningCalendlyLink, showCallMeNow } = usePlacementRecruiterScreeningConfig();
  const exitConfirmationModalState = useModalState();
  const worker = useDefinedWorker();
  const history = useHistory();
  const { showErrorToast } = useToast();

  const [step, setStep] = useState<ScreeningStep>("initial");

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phone: worker.phone ?? "",
    },
  });

  const isPendingScreening =
    placementCandidate?.screeningStatus === PlacementCandidateScreeningStatus.PENDING;

  const { mutateAsync: notifyRecruiterForScreening, isLoading: isNotifyingRecruiter } =
    useNotifyRecruiterForScreening({
      onSuccess: () => {
        setStep("success");
      },
      onError: (error: unknown) => {
        logError(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_NOTIFY_RECRUITER_FAILED, {
          error,
          metadata: {
            placementId,
            source,
            bookingReorderingCohort,
          },
        });
        showErrorToast("Failed to request call. Please try again.");
      },
    });

  const handleClose = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });
    exitConfirmationModalState.openModal();
  };

  const handleCallMeNow = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_CALL_ME_NOW_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });
    setStep("phone-confirmation");
  };

  const handlePhoneConfirm = async (data: PhoneFormData) => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_PHONE_CONFIRMED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });

    try {
      await notifyRecruiterForScreening({
        source,
        contactPhone: data.phone,
        ...(isDefined(placementId) && { placementId }),
        bookingReorderingCohort,
        ...(isDefined(selectedInterviewTime) && {
          interviewTime: selectedInterviewTime,
        }),
      });
    } catch {
      // Error is already handled by onError callback in the mutation hook
      // This catch ensures the error doesn't propagate and the failure is not treated as success
      // The loading state is automatically cleared by React Query on error
    }
  };

  const handleBack = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_BACK_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });
    setStep("initial");
  };

  const handleScheduleLater = async () => {
    if (!screeningCalendlyLink) {
      logError(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_SCHEDULE_LATER_MISSING_LINK, {
        error: "screeningCalendlyLink is missing",
        metadata: {
          placementId,
          source,
          bookingReorderingCohort,
        },
      });
      showErrorToast("Unable to schedule call. Please try again later.");
      return;
    }

    if (Capacitor.isNativePlatform()) {
      await Browser.open({
        url: screeningCalendlyLink,
        presentationStyle: "fullscreen",
      });
    } else {
      logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_SCHEDULE_LATER_CLICKED, {
        source,
        ...(isDefined(placementId) && { placementId }),
      });
      // For web platform, open in new tab
      window.open(screeningCalendlyLink, "_blank", "noopener,noreferrer");
    }

    modalState.closeModal();
  };

  const handleBrowseMoreJobs = () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_BROWSE_JOBS_CLICKED, {
      source,
      ...(isDefined(placementId) && { placementId }),
      bookingReorderingCohort,
    });
    modalState.closeModal();
    history.replace(PLACEMENT_PATHS.ROOT);
  };

  const isScreenFirst = bookingReorderingCohort === "screen_first";
  const exitDialogMessage = isScreenFirst
    ? "You&apos;ll need to complete a screening call before you can book interviews."
    : "Your selected interview time will not be reserved. You&apos;ll need to select a time again after completing your screening call.";

  return (
    <>
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        onClose={handleClose}
      >
        <Box sx={{ px: 5, py: 6 }}>
          {step === "initial" && (
            <InitialStepContent
              isPendingScreening={isPendingScreening}
              isNotifyingRecruiter={isNotifyingRecruiter}
              screeningCalendlyLink={screeningCalendlyLink}
              cohort={bookingReorderingCohort}
              showCallMeNow={showCallMeNow}
              onCallMeNow={handleCallMeNow}
              onScheduleLater={handleScheduleLater}
            />
          )}

          {step === "phone-confirmation" && (
            <PhoneConfirmationStepContent
              form={phoneForm}
              isNotifyingRecruiter={isNotifyingRecruiter}
              cohort={bookingReorderingCohort}
              onConfirm={handlePhoneConfirm}
              onBack={handleBack}
            />
          )}

          {step === "success" && (
            <SuccessStepContent
              cohort={bookingReorderingCohort}
              onBrowseMoreJobs={handleBrowseMoreJobs}
            />
          )}
        </Box>
      </BottomSheet>

      <CommonDialog
        modalState={exitConfirmationModalState}
        title={
          <Title component="h3" sx={{ fontWeight: 600 }}>
            Are you sure you want to exit?
          </Title>
        }
        actions={
          <Stack width="100%" spacing={1}>
            <Button
              variant="contained"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_DIALOG_CONFIRMED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                exitConfirmationModalState.closeModal();
                modalState.closeModal();
              }}
            >
              Yes, exit
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.PLACEMENT_SCREEN_LAST_STEP_EXIT_DIALOG_CANCELLED, {
                  source,
                  ...(isDefined(placementId) && { placementId }),
                  bookingReorderingCohort,
                });
                exitConfirmationModalState.closeModal();
              }}
            >
              Cancel
            </Button>
          </Stack>
        }
      >
        <Text sx={{ lineHeight: "24px" }} textAlign="center">
          {exitDialogMessage}
        </Text>
      </CommonDialog>
    </>
  );
}
